import { dbStorage } from './indexedDBStorage'
import { readLedgerSafePostgresPrimaryRecentScope } from './ledgerSafePostgresPrimaryReadBridge'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type {
  CreateLedgerSafeChapterSnapshotInput,
  LedgerSafeChapterSnapshot
} from '../types/ledgerSafeSnapshots'

const nowIso = (): string => new Date().toISOString()

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ledger-safe-snapshot-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const cloneMetadata = <T>(value: T | undefined): T | undefined => {
  if (value === undefined) {
    return undefined
  }
  return JSON.parse(JSON.stringify(value)) as T
}

const cloneRows = (rows: AnchoredRow[]): AnchoredRow[] =>
  rows.map(row => ({
    ...row,
    unitMappings: row.unitMappings.map(mapping => ({ ...mapping })),
    metadata: cloneMetadata(row.metadata)
  }))

export const createLedgerSafeChapterSnapshot = async (
  input: CreateLedgerSafeChapterSnapshotInput
): Promise<LedgerSafeChapterSnapshot> => {
  const snapshot: LedgerSafeChapterSnapshot = {
    id: generateId(),
    chapterId: input.chapterId,
    scopeId: input.scopeId,
    scopeLabel: input.scopeLabel,
    createdAt: nowIso(),
    label: input.label,
    reason: input.reason,
    rowCount: input.rows.length,
    rows: cloneRows(input.rows)
  }

  await dbStorage.put('ledger_safe_snapshots', snapshot)
  return snapshot
}

export const listLedgerSafeChapterSnapshots = async (
  chapterId: string,
  scopeId?: string,
  options: { limit?: number } = {},
): Promise<LedgerSafeChapterSnapshot[]> => {
  const limit = options.limit && options.limit > 0
    ? Math.floor(options.limit)
    : null
  const recentPrimarySnapshots = limit
    ? await readLedgerSafePostgresPrimaryRecentScope({
        databaseName: 'ScholarlyWorkflowDB',
        storeName: 'ledger_safe_snapshots',
        scope: { kind: 'chapter', id: chapterId },
        limit,
        maxBytes: 16_000_000,
        ...(scopeId ? { snapshotScopeId: scopeId } : {}),
      })
    : null
  const snapshots = recentPrimarySnapshots === null
    ? await dbStorage.getAllByIndex('ledger_safe_snapshots', 'chapterId', chapterId) as LedgerSafeChapterSnapshot[]
    : recentPrimarySnapshots as LedgerSafeChapterSnapshot[]
  const ordered = snapshots
    .filter(snapshot => !scopeId || snapshot.scopeId === scopeId)
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  return limit ? ordered.slice(0, limit) : ordered
}

export const getLatestLedgerSafeChapterSnapshot = async (
  chapterId: string,
  scopeId?: string
): Promise<LedgerSafeChapterSnapshot | null> => {
  const snapshots = await listLedgerSafeChapterSnapshots(chapterId, scopeId, { limit: 1 })
  return snapshots[0] || null
}
